import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  EnvironmentVariable,
  ResultantPolicy,
  ScheduledTask,
  Service,
  ShortcutProgram,
  StartupProgram,
} from '../../../../../Models/avyna/agent-device.model';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { AvTableColumn, AvTableDialogConfig, AvTableLinkCell, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [AvContentContainerComponent, AvTableCard],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly serviceColumns: AvTableColumn[] = [
    { key: 'serviceName', label: 'app.device.settings.columns.name', sortable: true },
    { key: 'serviceStatus', label: 'app.device.settings.columns.status', sortable: true },
    { key: 'details', label: 'app.device.settings.columns.details', type: 'link' },
  ];

  readonly scheduledTaskColumns: AvTableColumn[] = [
    { key: 'author', label: 'app.device.settings.columns.author', sortable: true },
    { key: 'account', label: 'app.device.settings.columns.account', sortable: true },
    { key: 'status', label: 'app.device.settings.columns.status', type: 'bool', sortable: true },
    { key: 'details', label: 'app.device.settings.columns.details', type: 'link' },
  ];

  readonly startupProgramColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.settings.columns.name', sortable: true },
    { key: 'enabled', label: 'app.device.settings.columns.enabled', type: 'bool', sortable: true },
    { key: 'startupType', label: 'app.device.settings.columns.startup_type', sortable: true },
    { key: 'details', label: 'app.device.settings.columns.details', type: 'link' },
  ];

  readonly shortcutProgramColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.settings.columns.name', sortable: true },
    { key: 'path', label: 'app.device.settings.columns.path', sortable: true },
    { key: 'details', label: 'app.device.settings.columns.details', type: 'link' },
  ];

  readonly environmentVariableColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.settings.columns.name', sortable: true },
    { key: 'value', label: 'app.device.settings.columns.value', sortable: true },
  ];

  readonly resultantPolicyColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.settings.columns.name', sortable: true },
    { key: 'source', label: 'app.device.settings.columns.source', sortable: true },
    { key: 'applied', label: 'app.device.settings.columns.applied', type: 'bool', sortable: true },
    { key: 'details', label: 'app.device.settings.columns.details', type: 'link' },
  ];

  readonly serviceRows = computed<AvTableRow[]>(() => {
    const services = this.device()?.agent?.settings?.services;
    if (!Array.isArray(services)) return [];

    return services.map((service: Service) => ({
      serviceName: this.fallbackString(service?.service_name),
      serviceStatus: this.formatServiceStatus(service?.service_status),
      details: this.detailsLink(this.buildServiceDialog(service)),
    }));
  });

  readonly scheduledTaskRows = computed<AvTableRow[]>(() => {
    const tasks = this.device()?.agent?.settings?.scheduled_tasks;
    if (!Array.isArray(tasks)) return [];

    return tasks.map((task: ScheduledTask) => ({
      taskName: this.fallbackString(task?.task_name),
      author: this.fallbackString(task?.author),
      status: this.formatTaskStatus(task?.status),
      account: this.fallbackString(task?.account),
      details: this.detailsLink(this.buildScheduledTaskDialog(task)),
    }));
  });

  readonly startupProgramRows = computed<AvTableRow[]>(() => {
    const programs = this.device()?.agent?.settings?.startup_programs;
    if (!Array.isArray(programs)) return [];

    return programs.map((program: StartupProgram) => ({
      name: this.fallbackString(program?.name),
      enabled: this.asBool(program?.enabled),
      startupType: this.formatStartupMode(program?.startup_type),
      details: this.detailsLink(this.buildStartupProgramDialog(program)),
    }));
  });

  readonly shortcutProgramRows = computed<AvTableRow[]>(() => {
    const programs = this.device()?.agent?.settings?.shortcut_programs;
    if (!Array.isArray(programs)) return [];

    return programs.map((program: ShortcutProgram) => ({
      name: this.fallbackString(program?.name),
      path: this.fallbackString(program?.path),
      details: this.detailsLink(this.buildShortcutProgramDialog(program)),
    }));
  });

  readonly environmentVariableRows = computed<AvTableRow[]>(() => {
    const vars = this.device()?.agent?.settings?.environment_variables;
    if (!Array.isArray(vars)) return [];

    return vars.map((variable: EnvironmentVariable) => ({
      name: this.fallbackString(variable?.name),
      value: this.fallbackString(variable?.value),
    }));
  });

  readonly resultantPolicyRows = computed<AvTableRow[]>(() => {
    const policies = this.device()?.agent?.settings?.resultant_set_of_policy;
    if (!Array.isArray(policies)) return [];

    return policies.map((policy: ResultantPolicy) => ({
      name: this.fallbackString(policy?.name),
      source: this.fallbackString(policy?.source),
      applied: this.asBool(policy?.applied),
      details: this.detailsLink(this.buildResultantPolicyDialog(policy)),
    }));
  });

  private detailsLink(dialog: AvTableDialogConfig): AvTableLinkCell {
    return {
      icon: 'more_horiz',
      ariaLabel: 'app.device.settings.columns.details',
      dialog,
    };
  }

  private buildScheduledTaskDialog(task: ScheduledTask): AvTableDialogConfig {
    return {
      titleKey: 'app.device.settings.dialog.scheduled_task_details.title',
      entries: [
        { labelKey: 'app.device.settings.details.task_name', value: task?.task_name },
        { labelKey: 'app.device.settings.columns.author', value: task?.author },
        { labelKey: 'app.device.settings.columns.status', value: task?.status },
        { labelKey: 'app.device.settings.columns.account', value: task?.account },
        { labelKey: 'app.device.settings.details.account_type', value: task?.account_type },
        { labelKey: 'app.device.settings.columns.command', value: task?.command },
        { labelKey: 'app.device.settings.details.argument', value: task?.argument },
      ],
    };
  }

  private buildServiceDialog(service: Service): AvTableDialogConfig {
    return {
      titleKey: 'app.device.settings.dialog.service_details.title',
      entries: [
        { labelKey: 'app.device.settings.columns.name', value: service?.service_name },
        { labelKey: 'app.device.settings.columns.display_name', value: service?.service_display_name },
        { labelKey: 'app.device.settings.columns.status', value: this.formatServiceStatus(service?.service_status) },
        { labelKey: 'app.device.settings.columns.startup_mode', value: this.formatStartupMode(service?.service_startup_mode) },
      ],
    };
  }

  private buildStartupProgramDialog(program: StartupProgram): AvTableDialogConfig {
    return {
      titleKey: 'app.device.settings.dialog.startup_program_details.title',
      entries: [
        { labelKey: 'app.device.settings.columns.name', value: program?.name },
        { labelKey: 'app.device.settings.columns.enabled', value: program?.enabled },
        { labelKey: 'app.device.settings.columns.startup_type', value: this.formatStartupMode(program?.startup_type) },
        { labelKey: 'app.device.settings.columns.command', value: program?.command },
        { labelKey: 'app.device.settings.details.location', value: program?.location },
      ],
    };
  }

  private buildShortcutProgramDialog(program: ShortcutProgram): AvTableDialogConfig {
    return {
      titleKey: 'app.device.settings.dialog.shortcut_program_details.title',
      entries: [
        { labelKey: 'app.device.settings.columns.name', value: program?.name },
        { labelKey: 'app.device.settings.columns.path', value: program?.path },
        { labelKey: 'app.device.settings.details.location', value: program?.location },
        { labelKey: 'app.device.settings.details.arguments', value: program?.arguments },
        { labelKey: 'app.device.settings.details.description', value: program?.description },
        { labelKey: 'app.device.settings.details.target_path', value: program?.target_path },
        { labelKey: 'app.device.settings.details.icon_location', value: program?.icon_location },
        { labelKey: 'app.device.settings.details.working_directory', value: program?.working_directory },
      ],
    };
  }

  private buildResultantPolicyDialog(policy: ResultantPolicy): AvTableDialogConfig {
    return {
      titleKey: 'app.device.settings.dialog.resultant_policy_details.title',
      entries: [
        { labelKey: 'app.device.settings.columns.name', value: policy?.name },
        { labelKey: 'app.device.settings.columns.source', value: policy?.source },
        { labelKey: 'app.device.settings.columns.applied', value: policy?.applied },
        { labelKey: 'app.device.settings.details.policy_type', value: policy?.policy_type },
      ],
    };
  }

  private fallbackString(value?: string | null): string {
    if (value && value.trim().length > 0) return value;
    return this.translate.instant('shared.general.labels.na');
  }

  private asBool(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
  }

  private formatTaskStatus(value?: string | null): boolean | null {
    if (!value) return null;
    const normalized = value.trim().toLowerCase();
    if (normalized === 'ready') return true;
    if (normalized === 'disabled') return false;
    return null;
  }

  private formatServiceStatus(value?: string | null): string {
    if (!value || !value.trim()) return this.translate.instant('shared.general.labels.na');
    const normalized = value.trim().toLowerCase();
    switch (normalized) {
      case 'running':
        return this.translate.instant('app.device.settings.values.service_status.running');
      case 'stopped':
        return this.translate.instant('app.device.settings.values.service_status.stopped');
      default:
        return this.translate.instant('shared.general.labels.na');
    }
  }

  private formatStartupMode(value?: string | null): string {
    if (!value || !value.trim()) return this.translate.instant('shared.general.labels.na');
    const normalized = value.trim().toLowerCase();
    const key = normalized === 'auto' || normalized === 'automatic'
      ? 'app.device.settings.values.startup_mode.auto'
      : normalized === 'manual'
        ? 'app.device.settings.values.startup_mode.manual'
        : null;
    return this.translateOrFallback(key, value);
  }

  private translateOrFallback(key: string | null, fallback: string): string {
    if (!key) return fallback;
    const translated = this.translate.instant(key);
    return translated === key ? fallback : translated;
  }
}
